export type ContentHeadingLanguage = "en" | "it" | "ar" | "tr";
export type ContentHeadingDomain =
  | "social_interaction"
  | "daily_life"
  | "services_public_interactions"
  | "communication_repair_problem_solving";

interface ContentHeadingLabels {
  chapter: string;
  domains: Record<ContentHeadingDomain, string>;
}

// Content vocabulary is independent of the supported app-shell locales.
const LABELS: Record<ContentHeadingLanguage, ContentHeadingLabels> = {
  en: {
    chapter: "Chapter",
    domains: {
      social_interaction: "Social interaction",
      daily_life: "Daily life",
      services_public_interactions: "Services / Public",
      communication_repair_problem_solving: "Communication repair",
    },
  },
  it: {
    chapter: "Capitolo",
    domains: {
      social_interaction: "Interazioni sociali",
      daily_life: "Vita quotidiana",
      services_public_interactions: "Servizi e uffici pubblici",
      communication_repair_problem_solving: "Chiarire e risolvere incomprensioni",
    },
  },
  ar: {
    chapter: "الفصل",
    domains: {
      social_interaction: "التفاعل الاجتماعي",
      daily_life: "الحياة اليومية",
      services_public_interactions: "الخدمات / المواقف العامة",
      communication_repair_problem_solving: "إصلاح الاتصالات",
    },
  },
  tr: {
    chapter: "Bölüm",
    domains: {
      social_interaction: "Sosyal etkileşim",
      daily_life: "Günlük hayat",
      services_public_interactions: "Hizmetler / Kamu",
      communication_repair_problem_solving: "İletişim onarımı",
    },
  },
};

export function getContentHeadingLabels(language: string): ContentHeadingLabels {
  const key = String(language || "").trim().toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(LABELS, key)) {
    throw new Error(`unsupported_content_heading_language:${key}`);
  }
  return LABELS[key as ContentHeadingLanguage];
}

export function getContentDomainTitle(domain: ContentHeadingDomain, language: string): string {
  return getContentHeadingLabels(language).domains[domain];
}

export function formatContentChapterHeading(language: string, chapterNumber: number, title: string): string {
  return `${getContentHeadingLabels(language).chapter} ${chapterNumber}: ${title.trim()}`;
}

export function getTargetContentCopy(language: string): Copy {
  const labels = getContentHeadingLabels(language);
  const base = getCopy(language);
  if (language.trim().toLowerCase() !== "it") return base;
  return {
    ...base,
    domainSocialInteraction: labels.domains.social_interaction,
    domainDailyLife: labels.domains.daily_life,
    domainServicesPublic: labels.domains.services_public_interactions,
    domainCommunicationRepair: labels.domains.communication_repair_problem_solving,
    practiceChapterLabel: labels.chapter,
    practiceLabel: "Pratica delle frasi",
    vocabularyLabel: "Vocabolario",
    dialogueLabel: "Missioni",
    modeCardPracticeContent: "Pratica delle frasi",
    modeCardVocabularyContent: "Pratica del vocabolario",
    modeCardMissionsContent: "Missioni",
    modeStatsCompleted: "Completati",
    vocabularyStudioTitle: "Studio del vocabolario",
    vocabularyExercisesTitle: "Esercizi di vocabolario",
    pickVocabularyTitle: "Pratica del vocabolario",
    pickVocabularyExercisesTitle: "Scegli un esercizio",
    vocabularyExerciseListenRepeatTitle: "Ascolta e ripeti",
    vocabularyExerciseListenRepeatHint: "Ascolta la parola e ripetila ad alta voce.",
    vocabularyExerciseUnscrambleTitle: "Riordina le lettere",
    vocabularyExerciseUnscrambleHint: "Metti le lettere nell'ordine giusto per formare la parola.",
    vocabularyExerciseListenWriteTitle: "Ascolta e scrivi",
    vocabularyExerciseListenWriteHint: "Scrivi la parola che senti.",
    practiceStartChapterAction: "Inizia la pratica",
    practiceReplayChapterAction: "Ripeti il capitolo",
    practiceNextChapterAction: "Capitolo successivo",
    practiceGoToMissionsAction: "Passa alle missioni",
    simulationSceneLabel: "Situazione",
    simulationGoalLabel: "Obiettivo",
    simulationTaskTitle: "Compito",
    simulationStartPromptInitiate: "Inizia la conversazione.",
    simulationStartPromptRespond: "Rispondi alla persona con cui parli.",
    simulationPreviewListenRequired: "Ascolta tutte le battute prima di iniziare.",
    simulationPreviewReadyButton: "Guarda il dialogo",
    mixedMissionsTitle: "Missioni miste",
    mixedMissionsPickerTitle: "Scegli una missione",
  };
}
import { getCopy, type Copy } from "./appCopy";
